import { COMPANY } from "../data/company";
import { withAppStoreCampaign, withPlayReferrer, withUtmTags } from "./attribution";
import { trackPixel } from "./metaPixel";
import { trackGoogleConversion } from "./googleAds";
import { trackGetApp } from "./track";

/**
 * One listener for every "get the app" link on the site, wherever it sits.
 *
 * Play links and web-app links are spread across a dozen components and the
 * generated content pages. Handling them here, at the moment of the click,
 * means a new button is counted without anyone remembering to wire it, and
 * the prerendered HTML keeps the plain store URL that search engines index.
 *
 * Links marked `data-signin` are for owners who already have an account, so
 * they are not counted as someone new asking for the app.
 */

const isPlay = (href) => href.startsWith(COMPANY.urls.playClub);
const isAppStore = (href) => href.startsWith(COMPANY.urls.appStoreClub);
const isWebApp = (href) => href.startsWith(COMPANY.urls.console);

export function handleGetAppClick(event) {
  // auxclick also fires for a right-click; only the middle button opens a tab.
  if (event.type === "auxclick" && event.button !== 1) return;
  const link = event.target?.closest?.("a[href]");
  if (!link) return;
  const href = link.getAttribute("href") || "";

  let target = null;
  if (isPlay(href)) {
    // Changed before the browser follows the link, so the tagged URL is the
    // one that opens.
    link.setAttribute("href", withPlayReferrer(href));
    target = "google_play";
  } else if (isAppStore(href)) {
    // Apple has no referrer; its campaign tags are the only way App Store
    // Connect can credit this download to the ad.
    link.setAttribute("href", withAppStoreCampaign(href, COMPANY.urls.appStoreProviderToken));
    target = "app_store";
  } else if (isWebApp(href) && !link.closest("[data-signin]")) {
    // The console is another domain: the tags go in the URL for it to read.
    link.setAttribute("href", withUtmTags(href));
    target = "web_app";
  }
  if (!target) return;

  trackPixel("Lead", { content_name: target });
  trackGoogleConversion();
  trackGetApp(target);
}

export function listenForGetAppClicks(doc = typeof document === "undefined" ? null : document) {
  if (!doc) return () => {};
  // Capture phase, so a component that stops propagation cannot hide a click.
  doc.addEventListener("click", handleGetAppClick, true);
  doc.addEventListener("auxclick", handleGetAppClick, true);
  return () => {
    doc.removeEventListener("click", handleGetAppClick, true);
    doc.removeEventListener("auxclick", handleGetAppClick, true);
  };
}
